import type { ClientInit } from '@sveltejs/kit';
import { ignore } from '@yp/common/async.ts';
import { isString, omit, pare } from '@yp/common/identity.ts';
import {
  AuthenticationError,
  initReportErrorContext,
  reportError,
} from '@yp/web.app/client/errors.ts';
import { InfoModal } from '@yp/web.app/gui/info_modal.ts';
import { DEV } from 'esm-env';
import type { ComponentProps } from 'svelte';
import { modals } from 'svelte-modals';
import { createContext, FireContext, Session } from '../context.ts';
import { onTokenChanged } from './auth.ts';

// TODO: import this type from firebase
type FirebaseConfig = {
  projectId: string;
  appId: string;
  databaseURL?: string;
  storageBucket: string;
  locationId: string;
  apiKey: string;
  authDomain: string;
  messagingSenderId: string;
  measurementId: string;
};

type ClientConfig = {
  appName: string;
  version: string;
  emailDomain: string;
  firebase: FirebaseConfig;
  recaptchaSiteKey?: string;
};

export function initClient(cfg: ClientConfig): ClientInit {
  const handler: ClientInit = async () => {
    // __registerGlobalInspect();

    // The compat layer utilizes global 'firebase', so avoid a name collision.
    await createContext(
      cfg.firebase,
      pare({
        emailDomain: cfg.emailDomain,
        // Recaptcha v3
        // https://www.google.com/recaptcha/admin/site/724663564
        // Recaptcha Enterprise
        // DNS: Remove 'localhost' from this key's authorized domains and use a separate test key for
        // DEV.
        // https://console.cloud.google.com/security/recaptcha/6LdlXTErAAAAAE5wZoAI0iOUIcj_cdRPkDCJh9bg/overview
        recaptchaSiteKey: cfg.recaptchaSiteKey,
        cacheResults: true,
        onTokenChanged,
      }),
    );
    console.log('createContext done.');

    initReportErrorContext(async () => {
      const uid = (await Session.load())?.user?.uid;
      return pare({
        app: cfg.appName,
        version: cfg.version,
        uid: uid ?? undefined,
      });
    });

    // PwaInstaller.instance().canInstall.subscribe((value) => {
    //   console.log('Ready to install:', value);
    // });

    window.onerror = (event, source, lineno, colno, error) => {
      if (source?.includes('recaptcha__en.js') ?? false) {
        console.warn('Recaptcha error', error);
        return true;
      }
      console.error('onerror', { event, source, lineno, colno, error });
      ignore(
        reportError(error ?? new Error(), {
          via: 'onerror',
          extra: {
            event: isString(event) ? event : event.type,
            source,
            lineno,
          },
        }),
      );
      return undefined;
    };

    window.onunhandledrejection = (event) => {
      if (event instanceof PromiseRejectionEvent && event.reason instanceof AuthenticationError) {
        event.preventDefault();
        console.log('Authentication error. Signing out.');
        ignore(
          FireContext.load()
            .then((context) => context.auth.signOut())
            .finally(() => {
              modals.closeAll();
              const props: ComponentProps<InfoModal> = {
                header: 'You were unexpectedly signed out.',
              };
              modals.open(InfoModal, omit(props, 'isOpen'));
            }),
        );
        return;
      }
      if (event instanceof PromiseRejectionEvent && event.reason === null) {
        if (DEV) {
          // TODO: Is there a way to read/test the stack trace of a PromiseRejectionEvent in general?
          // In the case shown here, the stack would contain recaptcha.
          console.error('https://stackoverflow.com/a/56271040');
        }
      }
      const error: Error =
        event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      console.error('onunhandledrejection', { event });
      ignore(reportError(error, { via: event.type }));
    };
  };

  return handler;
}
